// Clips API functions
import apiClient, { unwrapResponse } from './client';
import type {
  AudioClip,
  ClipsQueryParams,
  CreateClipRequest,
  UpdateClipRequest,
  PaginatedResponse,
  PaginationMeta,
  SuccessEnvelope,
  Actor,
  Tag
} from './types';

export const clipsApi = {
  /**
   * List/search audio clips with optional filters
   */
  async listClips(params: ClipsQueryParams = {}): Promise<{ clips: AudioClip[]; meta: PaginationMeta }> {
    const response = await apiClient.get<PaginatedResponse<AudioClip>>('/clips', { params });
    if (response.data.ok) {
      return {
        clips: response.data.data,
        meta: response.data.meta,
      };
    }
    throw new Error('Failed to fetch clips');
  },

  /**
   * Get a single clip by ID
   */
  async getClip(id: number): Promise<AudioClip> {
    const response = await apiClient.get<SuccessEnvelope<AudioClip>>(`/clips/${id}`);
    return unwrapResponse(response);
  },

  /**
   * Get the streaming URL for a clip
   */
  getStreamUrl(id: number): string {
    return `/api/v1/clips/${id}/stream`;
  },

  /**
   * Create a new clip (admin only)
   */
  async createClip(data: CreateClipRequest): Promise<AudioClip> {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('actor_id', String(data.actor_id));
    
    if (data.tag_ids) {
      data.tag_ids.forEach((id) => formData.append('tag_ids[]', String(id)));
    }
    if (data.transcription_tamil) {
      formData.append('transcription_tamil', data.transcription_tamil);
    }
    if (data.transcription_english) {
      formData.append('transcription_english', data.transcription_english);
    }
    if (data.transcription_tanglish) {
      formData.append('transcription_tanglish', data.transcription_tanglish);
    }
    formData.append('audio_file', data.audio_file);

    // Note: Don't set Content-Type header manually - axios will set it with proper boundary
    const response = await apiClient.post<SuccessEnvelope<AudioClip>>('/clips', formData);
    return unwrapResponse(response);
  },

  /**
   * Update clip metadata (admin only)
   */
  async updateClip(id: number, data: UpdateClipRequest): Promise<AudioClip> {
    const response = await apiClient.patch<SuccessEnvelope<AudioClip>>(`/clips/${id}`, data);
    return unwrapResponse(response);
  },

  /**
   * Delete a clip (admin only)
   */
  async deleteClip(id: number): Promise<void> {
    await apiClient.delete(`/clips/${id}`);
  },

  /**
   * Toggle favorite status for a clip
   */
  async toggleFavorite(id: number): Promise<boolean> {
    const response = await apiClient.post<SuccessEnvelope<{ is_favorite: boolean }>>(`/clips/${id}/favorite`);
    return unwrapResponse(response).is_favorite;
  },

  /**
   * Get user's favorite clips
   */
  async getFavorites(page = 1, perPage = 20): Promise<{ clips: AudioClip[]; meta: PaginationMeta }> {
    const response = await apiClient.get<PaginatedResponse<AudioClip>>('/users/favorites', {
      params: { page, per_page: perPage },
    });
    if (response.data.ok) {
      return {
        clips: response.data.data,
        meta: response.data.meta,
      };
    }
    throw new Error('Failed to fetch favorites');
  },

  /**
   * Reorder user's favorite clips
   */
  async reorderFavorites(clipIds: number[]): Promise<void> {
    await apiClient.patch('/users/favorites/reorder', { clip_ids: clipIds });
  },

  /**
   * List all actors
   */
  async listActors(): Promise<Actor[]> {
    const response = await apiClient.get<SuccessEnvelope<Actor[]>>('/actors');
    return unwrapResponse(response);
  },

  /**
   * Create a new actor (admin only)
   */
  async createActor(data: { name: string; name_tamil?: string }): Promise<Actor> {
    const response = await apiClient.post<SuccessEnvelope<Actor>>('/actors', data);
    return unwrapResponse(response);
  },

  /**
   * Update an actor (admin only)
   */
  async updateActor(id: number, data: { name?: string; name_tamil?: string }): Promise<Actor> {
    const response = await apiClient.patch<SuccessEnvelope<Actor>>(`/actors/${id}`, data);
    return unwrapResponse(response);
  },

  /**
   * Delete an actor (admin only)
   */
  async deleteActor(id: number): Promise<void> {
    await apiClient.delete(`/actors/${id}`);
  },

  /**
   * List all tags
   */
  async listTags(): Promise<Tag[]> {
    const response = await apiClient.get<SuccessEnvelope<Tag[]>>('/tags');
    return unwrapResponse(response);
  },

  /**
   * Create a new tag (admin only)
   */
  async createTag(data: { name: string }): Promise<Tag> {
    const response = await apiClient.post<SuccessEnvelope<Tag>>('/tags', data);
    return unwrapResponse(response);
  },

  /**
   * Update a tag (admin only)
   */
  async updateTag(id: number, data: { name?: string }): Promise<Tag> {
    const response = await apiClient.patch<SuccessEnvelope<Tag>>(`/tags/${id}`, data);
    return unwrapResponse(response);
  },

  /**
   * Delete a tag (admin only)
   */
  async deleteTag(id: number): Promise<void> {
    await apiClient.delete(`/tags/${id}`);
  },
};

export default clipsApi;
